import type { Stripe } from '@stripe/stripe-js'
import { loadStripe } from '@stripe/stripe-js/pure'
import { getStripeCredentials } from 'api/order'

interface StripeCredentials {
  stripe_publishable_key: string
  stripe_account: string
  charge_descriptors?: Record<string, string>
}

export interface StripeInstanceResult {
  charge_descriptors?: Record<string, string>
  stripe: Stripe | null
}

type LoadStripeOptions = NonNullable<Parameters<typeof loadStripe>[1]>

const stripeInstanceCache = new Map<string, Promise<Stripe | null>>()

const stripeCredentialsCache = new Map<string, Promise<StripeCredentials>>()

function getCurrencyCacheKey(currency: string): string {
  return currency.trim().toUpperCase()
}

function fetchStripeCredentials(currency: string): Promise<StripeCredentials> {
  const cacheKey = getCurrencyCacheKey(currency)

  if (!stripeCredentialsCache.has(cacheKey)) {
    const request = getStripeCredentials(cacheKey).catch((error) => {
      stripeCredentialsCache.delete(cacheKey)
      throw error
    })

    stripeCredentialsCache.set(cacheKey, request)
  }

  return stripeCredentialsCache.get(cacheKey)!
}

export function clearCachedStripeCredentials(currency?: string): void {
  if (!currency) {
    stripeCredentialsCache.clear()
    return
  }

  stripeCredentialsCache.delete(getCurrencyCacheKey(currency))
}

function getStripeCacheKey(publishableKey: string, options?: LoadStripeOptions): string {
  return JSON.stringify([
    publishableKey,
    options?.stripeAccount ?? null,
    options?.betas ?? null,
  ])
}

function getCachedStripe(
  publishableKey: string,
  options?: LoadStripeOptions,
): Promise<Stripe | null> {
  const cacheKey = getStripeCacheKey(publishableKey, options)

  if (!stripeInstanceCache.has(cacheKey)) {
    const request = loadStripe(publishableKey, options).catch((error) => {
      stripeInstanceCache.delete(cacheKey)
      throw error
    })

    stripeInstanceCache.set(cacheKey, request)
  }

  return stripeInstanceCache.get(cacheKey)!
}

export function clearCachedStripe(
  publishableKey?: string,
  options?: LoadStripeOptions,
): void {
  if (!publishableKey) {
    stripeInstanceCache.clear()
    return
  }

  stripeInstanceCache.delete(getStripeCacheKey(publishableKey, options))
}

async function getStripeInstance(
  currency: string,
): Promise<StripeInstanceResult> {
  const credentials = await fetchStripeCredentials(currency)

  const stripeInstance = await getCachedStripe(credentials.stripe_publishable_key, {
    stripeAccount: credentials.stripe_account,
  }) ?? null

  return {
    charge_descriptors: credentials.charge_descriptors,
    stripe: stripeInstance,
  }
}

export default getStripeInstance
